import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertCircle,
  Check,
  CheckCircle2,
  ChevronDown,
  Clock,
  FilePenLine,
  Flag,
  Loader2,
  MoreHorizontal,
  Pencil,
  Phone,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { MakeCallModal } from '@/components/common/calls/make-call-modal';
import { AddProviderModal } from '@/components/provider-directory/AddProviderModal';
import { DatePicker } from '@/components/tasks/DatePicker';
import {
  barrierTypes,
  treatmentFrequencyInfo,
  treatmentProgressInfo,
  treatmentStatusInfo,
} from '@/components/treatment/check-ins/constants';
import { useTreatmentCheckInPush } from '@/components/treatment/check-ins/hooks/useTreatmentCheckInPush';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useMatter } from '@/hooks/useMatter';
import { useGetIncidentsQuery } from '@/services/api/incidentService';
import { useGetMatterQuery } from '@/services/api/mattersService';
import {
  useAddProviderUpdateToCheckInMutation,
  useApproveMedicalProviderMutation,
  useCreateTreatmentBarrierMutation,
  useCreateTreatmentCheckInMutation,
  useDeleteProviderUpdateFromCheckInMutation,
  useDeleteTreatmentBarrierMutation,
  useDeleteTreatmentCheckInMutation,
  useFinalizeTreatmentCheckInMutation,
  useGetMedicalProvidersQuery,
  useGetReviewFoundProvidersQuery,
  useGetTreatmentBarriersQuery,
  useGetTreatmentCheckInsQuery,
  useRejectMedicalProviderMutation,
  useSaveTreatmentCheckInDraftMutation,
  useUpdateTreatmentBarrierMutation,
} from '@/services/api/treatmentService';
import {
  ProviderStatus,
  ProviderTreatmentStatus,
  TreatmentBarrier,
  TreatmentBarrierStatus,
  TreatmentBarrierType,
  TreatmentCheckIn,
  TreatmentCheckInStatus,
  TreatmentFrequency,
  TreatmentProgress,
} from '@/services/types/treatment-types';

// Form schema
const providerUpdateSchema = z.object({
  updateId: z.string(),
  providerId: z.string().min(1),
  providerName: z.string().min(1),
  treatmentStatus: z.nativeEnum(ProviderTreatmentStatus).nullable(),
  treatmentProgress: z.nativeEnum(TreatmentProgress).optional().nullable(),
  treatmentFrequency: z.nativeEnum(TreatmentFrequency).optional().nullable(),
  painLevel: z.union([z.string(), z.number()]).nullish(),
  notes: z.string().nullish(),
  lastAppointment: z.string().nullish(),
});

const formSchema = z.object({
  generalNotes: z
    .string()
    .min(1, 'Call notes are required')
    .nullable()
    .refine((val) => val && val.trim().length > 0, {
      message: 'Call notes are required',
    }),
  providerUpdates: z.array(providerUpdateSchema),
});

type FormValues = z.infer<typeof formSchema>;

interface CheckInModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CheckInModal = ({ open, onOpenChange }: CheckInModalProps) => {
  const { matterId } = useMatter();
  const [checkInId, setCheckInId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddProviderModalOpen, setIsAddProviderModalOpen] = useState(false);
  const [showFlagIssue, setShowFlagIssue] = useState(false);
  const [barrierType, setBarrierType] = useState<TreatmentBarrierType | ''>('');
  const [barrierNotes, setBarrierNotes] = useState('');
  const [editingIssue, setEditingIssue] = useState<TreatmentBarrier | null>(
    null,
  );
  const [editingIssueNotes, setEditingIssueNotes] = useState('');
  const [lastCheckInExpanded, setLastCheckInExpanded] = useState(false);
  const [draftStatus, setDraftStatus] = useState<'idle' | 'saving' | 'saved'>(
    'idle',
  );
  const [isInitializing, setIsInitializing] = useState(false);
  const [isCallModalOpen, setIsCallModalOpen] = useState(false);
  const hasInitializedRef = useRef(false);

  // Queries
  const { data: matterData } = useGetMatterQuery(matterId);
  const { data: existingProviders } = useGetMedicalProvidersQuery(matterId);
  const { data: reviewFoundProviders, isFetching: isFetchingReviewFound } =
    useGetReviewFoundProvidersQuery(
      { matterId, limit: 50 },
      {
        skip: !open,
      },
    );
  const { data: checkIns } = useGetTreatmentCheckInsQuery(matterId);
  const { data: barriers } = useGetTreatmentBarriersQuery({ matterId });
  const { data: incidents } = useGetIncidentsQuery(
    { matterId },
    { skip: !isAddProviderModalOpen },
  );

  const incidentCenter = useMemo(() => {
    const list = incidents ?? [];
    const best =
      list.find(
        (i) =>
          i.normalized_location?.latitude != null &&
          i.normalized_location?.longitude != null,
      ) ??
      list.find(
        (i) => i.extracted_latitude != null && i.extracted_longitude != null,
      );
    if (!best) return null;
    const lat = best.normalized_location?.latitude ?? best.extracted_latitude;
    const lng = best.normalized_location?.longitude ?? best.extracted_longitude;
    if (lat == null || lng == null) return null;
    return { lat, lng };
  }, [incidents]);

  // Get client's primary phone number
  const clientPhone = useMemo(() => {
    const client = matterData?.clients?.[0];
    return client?.phone_numbers?.[0]?.phone_number ?? null;
  }, [matterData]);

  const clientName = matterData?.clients?.[0]?.name ?? 'Patient';

  // Mutations
  const [createCheckIn] = useCreateTreatmentCheckInMutation();
  const [addProviderUpdate] = useAddProviderUpdateToCheckInMutation();
  const [deleteProviderUpdate] = useDeleteProviderUpdateFromCheckInMutation();
  const [saveDraft] = useSaveTreatmentCheckInDraftMutation();
  const [finalizeCheckIn] = useFinalizeTreatmentCheckInMutation();
  const [approveMedicalProvider, { isLoading: isApprovingProvider }] =
    useApproveMedicalProviderMutation();
  const [rejectMedicalProvider, { isLoading: isRejectingProvider }] =
    useRejectMedicalProviderMutation();
  const [createBarrier] = useCreateTreatmentBarrierMutation();
  const [updateBarrier] = useUpdateTreatmentBarrierMutation();
  const [deleteBarrier] = useDeleteTreatmentBarrierMutation();
  const [deleteCheckIn] = useDeleteTreatmentCheckInMutation();
  const { pushCheckInChanges } = useTreatmentCheckInPush({ matterId });

  // Get draft check-ins sorted by date
  const draftCheckIns = useMemo(() => {
    if (!checkIns) return [];
    return checkIns
      .filter((c) => c.status === TreatmentCheckInStatus.DRAFT)
      .sort(
        (a, b) =>
          new Date(b.modified_at).getTime() - new Date(a.modified_at).getTime(),
      );
  }, [checkIns]);

  // Get completed check-ins sorted by date
  const completedCheckIns = useMemo(() => {
    if (!checkIns) return [];
    return checkIns
      .filter(
        (c) => c.status === TreatmentCheckInStatus.COMPLETED && c.check_in_time,
      )
      .sort(
        (a, b) =>
          new Date(b.check_in_time!).getTime() -
          new Date(a.check_in_time!).getTime(),
      );
  }, [checkIns]);

  const lastCheckIn = completedCheckIns[0] ?? null;

  // Get approved providers
  const approvedProviders = useMemo(() => {
    return (
      existingProviders?.filter((p) => p.status === ProviderStatus.APPROVED) ??
      []
    );
  }, [existingProviders]);

  // Active barriers
  const activeBarriers = useMemo(() => {
    return (
      barriers?.filter((b) => b.status !== TreatmentBarrierStatus.RESOLVED) ??
      []
    );
  }, [barriers]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      generalNotes: '',
      providerUpdates: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'providerUpdates',
  });

  // Load a draft into the form
  const loadDraft = useCallback(
    (draft: TreatmentCheckIn) => {
      setCheckInId(draft.id);
      // Deduplicate provider updates by provider_id (keep first occurrence).
      // Note: provider updates without provider_id are ignored (UI requires linking).
      const seenProviderIds = new Set<string>();
      const deduplicatedUpdates =
        draft.provider_updates?.filter((u) => {
          if (!u.provider_id) return false;
          if (seenProviderIds.has(u.provider_id)) return false;
          seenProviderIds.add(u.provider_id);
          return true;
        }) ?? [];

      form.reset({
        generalNotes: draft.general_notes ?? '',
        providerUpdates: deduplicatedUpdates.map((u) => ({
          updateId: u.id,
          providerId: u.provider_id ?? '',
          providerName: u.provider_name ?? '',
          treatmentStatus: u.new_treatment_status,
          treatmentProgress: u.new_treatment_progress,
          treatmentFrequency: u.new_treatment_frequency,
          painLevel: u.new_pain_level,
          notes: u.new_treatment_notes,
          lastAppointment: u.new_treatment_status_occurred_at,
        })),
      });
    },
    [form],
  );

  // Create a new check-in
  const createNewCheckIn = useCallback(async () => {
    try {
      const checkIn = await createCheckIn({ matterId }).unwrap();
      setCheckInId(checkIn.id);

      if (approvedProviders.length > 0) {
        const updates = await Promise.all(
          approvedProviders.map((provider) =>
            addProviderUpdate({
              matterId,
              checkInId: checkIn.id,
              providerUpdate: {
                check_in_id: checkIn.id,
                provider_id: provider.id,
                provider_name: provider.name,
              },
            }).unwrap(),
          ),
        );

        form.reset({
          generalNotes: '',
          providerUpdates: updates.map((u) => ({
            updateId: u.id,
            providerId: u.provider_id ?? '',
            providerName: u.provider_name ?? '',
            treatmentStatus: u.new_treatment_status,
            treatmentProgress: u.new_treatment_progress,
            treatmentFrequency: u.new_treatment_frequency,
            painLevel: u.new_pain_level,
            notes: u.new_treatment_notes,
            lastAppointment: u.new_treatment_status_occurred_at,
          })),
        });
      }
    } catch {
      toast.error('Failed to start check-in');
      onOpenChange(false);
    }
  }, [
    matterId,
    approvedProviders,
    createCheckIn,
    addProviderUpdate,
    form,
    onOpenChange,
  ]);

  // Initialize: load most recent draft or create new (only once when modal opens)
  useEffect(() => {
    const initialize = async () => {
      if (
        open &&
        !hasInitializedRef.current &&
        !isInitializing &&
        checkIns !== undefined
      ) {
        hasInitializedRef.current = true;
        setIsInitializing(true);
        try {
          if (draftCheckIns.length > 0) {
            // Load the most recent draft
            loadDraft(draftCheckIns[0]);
          } else {
            // Create a new check-in
            await createNewCheckIn();
          }
        } finally {
          setIsInitializing(false);
        }
      }
    };
    initialize();
  }, [
    open,
    checkIns,
    draftCheckIns,
    loadDraft,
    createNewCheckIn,
    isInitializing,
  ]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setCheckInId(null);
      setShowFlagIssue(false);
      setBarrierType('');
      setBarrierNotes('');
      setLastCheckInExpanded(false);
      setIsInitializing(false);
      hasInitializedRef.current = false;
      form.reset({ generalNotes: '', providerUpdates: [] });
    }
  }, [open, form]);

  // Auto-save
  const debouncedSave = useCallback(async () => {
    if (!checkInId) return;
    setDraftStatus('saving');
    const values = form.getValues();
    const { error } = await saveDraft({
      matterId,
      checkInId,
      payload: {
        check_in_id: checkInId,
        general_notes: values.generalNotes,
        updates: values.providerUpdates.map((p) => ({
          id: p.updateId,
          check_in_id: checkInId,
          provider_id: p.providerId,
          provider_name: p.providerName,
          new_treatment_status: p.treatmentStatus,
          new_treatment_frequency: p.treatmentFrequency,
          new_treatment_progress: p.treatmentProgress,
          new_pain_level: p.painLevel ? Number(p.painLevel) : null,
          new_treatment_notes: p.notes,
          new_treatment_status_occurred_at: p.lastAppointment,
        })),
      },
    });
    if (!error) {
      setDraftStatus('saved');
      setTimeout(() => setDraftStatus('idle'), 2000);
    } else {
      setDraftStatus('idle');
    }
  }, [checkInId, matterId, saveDraft, form]);

  useEffect(() => {
    const subscription = form.watch(() => {
      setDraftStatus('idle');
      const timeoutId = setTimeout(debouncedSave, 1000);
      return () => clearTimeout(timeoutId);
    });
    return () => subscription.unsubscribe();
  }, [form, debouncedSave]);

  const handleAddProvider = () => {
    if (!checkInId) return;
    setIsAddProviderModalOpen(true);
  };

  const handleRemoveProvider = async (index: number) => {
    const { updateId } = fields[index];
    remove(index);
    if (checkInId) {
      await deleteProviderUpdate({
        matterId,
        checkInId,
        providerUpdateId: updateId,
      });
    }
  };

  const handleAddBarrier = async () => {
    if (!barrierType || !barrierNotes || !checkInId) return;
    const { error } = await createBarrier({
      matterId,
      payload: {
        barrier_type: barrierType,
        notes: barrierNotes,
        discovered_in_check_in_id: checkInId,
      },
    });
    if (error) {
      toast.error('Failed to flag issue');
    } else {
      setBarrierType('');
      setBarrierNotes('');
      setShowFlagIssue(false);
      toast.success('Issue flagged');
    }
  };

  const handleUpdateIssue = (issue: TreatmentBarrier) => {
    setEditingIssue(issue);
    setEditingIssueNotes(issue.notes);
  };

  const handleSaveIssueUpdate = async () => {
    if (!editingIssue) return;
    const { error } = await updateBarrier({
      matterId,
      barrierId: editingIssue.id,
      payload: { id: editingIssue.id, notes: editingIssueNotes },
    });
    if (error) {
      toast.error('Failed to update issue');
    } else {
      toast.success('Issue updated');
      setEditingIssue(null);
      setEditingIssueNotes('');
    }
  };

  const handleResolveIssue = async (issue: TreatmentBarrier) => {
    const { error } = await updateBarrier({
      matterId,
      barrierId: issue.id,
      payload: { id: issue.id, status: TreatmentBarrierStatus.RESOLVED },
    });
    if (error) {
      toast.error('Failed to resolve issue');
    } else {
      toast.success('Issue resolved');
    }
  };

  const handleDeleteIssue = async (barrierId: string) => {
    const { error } = await deleteBarrier({
      matterId,
      barrierId,
    });
    if (error) {
      toast.error('Failed to delete issue');
    }
  };

  const onSubmit = async (values: FormValues) => {
    if (!checkInId) return;
    setIsSubmitting(true);
    try {
      const { error } = await finalizeCheckIn({
        matterId,
        checkInId,
        payload: {
          check_in_id: checkInId,
          general_notes: values.generalNotes,
          updates: values.providerUpdates.map((p) => ({
            id: p.updateId,
            check_in_id: checkInId,
            provider_id: p.providerId,
            provider_name: p.providerName,
            new_treatment_status: p.treatmentStatus,
            new_treatment_frequency: p.treatmentFrequency,
            new_treatment_progress: p.treatmentProgress,
            new_pain_level: p.painLevel ? Number(p.painLevel) : null,
            new_treatment_notes: p.notes,
            new_treatment_status_occurred_at: p.lastAppointment,
          })),
        },
      });
      if (error) {
        toast.error('Failed to submit check-in');
      } else {
        toast.success('Check-in logged');

        // Push changes to external tasks (general notes as comment, due date +2 weeks)
        await pushCheckInChanges({ generalNotes: values.generalNotes });

        onOpenChange(false);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteDraft = async (e: React.MouseEvent, draftId: string) => {
    e.stopPropagation();
    const { error } = await deleteCheckIn({ matterId, checkInId: draftId });
    if (error) {
      toast.error('Failed to delete draft');
    } else if (draftId === checkInId) {
      // If we deleted the current draft, reset to create a new one or load another
      setCheckInId(null);
    }
  };

  const handleApproveReviewProvider = async (providerId: string) => {
    try {
      await approveMedicalProvider({ matterId, providerId }).unwrap();
      toast.success('Provider approved');
    } catch {
      toast.error('Failed to approve provider');
    }
  };

  const handleRejectReviewProvider = async (providerId: string) => {
    try {
      await rejectMedicalProvider({ matterId, providerId }).unwrap();
      toast.success('Provider rejected');
    } catch {
      toast.error('Failed to reject provider');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col p-0 shadow-none">
        <DialogHeader className="px-5 py-3 border-b flex-shrink-0">
          <div className="flex items-center justify-between pr-8">
            <div className="flex items-center gap-3">
              <DialogTitle className="text-sm font-semibold">
                Log Treatment Check-in
              </DialogTitle>
              <AnimatePresence mode="wait">
                {draftStatus === 'saving' && (
                  <motion.div
                    key="saving"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center gap-1 text-[10px] text-zinc-500"
                  >
                    <Loader2 className="h-2.5 w-2.5 animate-spin" />
                    Saving...
                  </motion.div>
                )}
                {draftStatus === 'saved' && (
                  <motion.div
                    key="saved"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center gap-1 text-[10px] text-green-600"
                  >
                    <Check className="h-2.5 w-2.5" />
                    Draft saved
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <div className="flex items-center gap-2">
              {/* Draft selector */}
              {draftCheckIns.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs gap-1"
                    >
                      <FilePenLine className="h-3 w-3" />
                      Drafts ({draftCheckIns.length})
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64">
                    {draftCheckIns.map((draft) => (
                      <DropdownMenuItem
                        key={draft.id}
                        className={`flex items-center justify-between px-2 py-1.5 text-xs cursor-pointer ${checkInId === draft.id ? 'bg-zinc-100' : ''}`}
                        onClick={() => loadDraft(draft)}
                      >
                        <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                          <span className="font-medium">
                            {format(
                              new Date(draft.modified_at),
                              'MMM d, h:mm a',
                            )}
                            {checkInId === draft.id && ' (current)'}
                          </span>
                          {draft.general_notes && (
                            <span className="text-zinc-500 line-clamp-1">
                              {draft.general_notes}
                            </span>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleDeleteDraft(e, draft.id)}
                          className="h-5 w-5 p-0 text-zinc-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0 ml-2"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuItem
                      onClick={createNewCheckIn}
                      className="text-xs border-t mt-1 pt-1"
                    >
                      <Plus className="h-3 w-3 mr-1.5" />
                      New Check-in
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col flex-1 overflow-hidden"
          >
            <div className="flex-1 overflow-y-auto">
              <div className="grid grid-cols-3 divide-x min-h-full">
                {/* Left column - Context */}
                <div className="col-span-1 p-3 bg-zinc-50/50 space-y-3">
                  {/* Patient phone */}
                  {clientPhone && (
                    <div>
                      <h3 className="text-[10px] font-medium text-zinc-700 uppercase tracking-wide mb-1.5">
                        Patient
                      </h3>
                      <div className="bg-white rounded border p-2">
                        <div className="text-[11px] font-medium text-zinc-900 mb-1">
                          {clientName}
                        </div>
                        <button
                          type="button"
                          onClick={() => setIsCallModalOpen(true)}
                          className="flex items-center gap-1.5 text-[11px] text-violet-600 hover:text-violet-700 hover:underline"
                        >
                          <Phone className="h-3 w-3" />
                          {clientPhone}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Last check-in */}
                  <div>
                    <h3 className="text-[10px] font-medium text-zinc-700 uppercase tracking-wide mb-1.5">
                      Last Check-in
                    </h3>
                    {lastCheckIn ? (
                      <div className="bg-white rounded border p-2 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1 text-[10px] text-zinc-700">
                            <Clock className="h-2.5 w-2.5" />
                            {format(
                              new Date(lastCheckIn.check_in_time!),
                              'MMM d, yyyy',
                            )}
                          </div>
                          {lastCheckIn.general_notes &&
                            lastCheckIn.general_notes.length > 100 && (
                              <button
                                type="button"
                                onClick={() =>
                                  setLastCheckInExpanded(!lastCheckInExpanded)
                                }
                                className="text-[10px] text-zinc-500 hover:text-zinc-700"
                              >
                                {lastCheckInExpanded ? 'Less' : 'More'}
                              </button>
                            )}
                        </div>
                        {lastCheckIn.general_notes && (
                          <p
                            className={`text-[11px] text-zinc-900 ${lastCheckInExpanded ? '' : 'line-clamp-3'}`}
                          >
                            {lastCheckIn.general_notes}
                          </p>
                        )}
                        {lastCheckIn.provider_updates &&
                          lastCheckIn.provider_updates.length > 0 && (
                            <div className="pt-1.5 border-t space-y-0.5">
                              {(lastCheckInExpanded
                                ? lastCheckIn.provider_updates
                                : lastCheckIn.provider_updates.slice(0, 3)
                              ).map((pu) => (
                                <div
                                  key={pu.id}
                                  className="text-[10px] text-zinc-700"
                                >
                                  <span className="font-medium">
                                    {pu.provider_name}
                                  </span>
                                  {pu.new_treatment_status && (
                                    <span className="text-zinc-500">
                                      {' '}
                                      ·{' '}
                                      {
                                        treatmentStatusInfo[
                                          pu.new_treatment_status
                                        ]?.label
                                      }
                                    </span>
                                  )}
                                </div>
                              ))}
                              {!lastCheckInExpanded &&
                                lastCheckIn.provider_updates.length > 3 && (
                                  <button
                                    type="button"
                                    onClick={() => setLastCheckInExpanded(true)}
                                    className="text-[10px] text-zinc-500 hover:text-zinc-700"
                                  >
                                    +{lastCheckIn.provider_updates.length - 3}{' '}
                                    more
                                  </button>
                                )}
                            </div>
                          )}
                      </div>
                    ) : (
                      <p className="text-[10px] text-zinc-500">
                        No previous check-ins
                      </p>
                    )}
                  </div>

                  {/* Recent history */}
                  {completedCheckIns.length > 1 && (
                    <div>
                      <h3 className="text-[10px] font-medium text-zinc-700 uppercase tracking-wide mb-1.5">
                        Recent History
                      </h3>
                      <div className="space-y-1.5">
                        {completedCheckIns.slice(1, 4).map((ci) => (
                          <div
                            key={ci.id}
                            className="bg-white rounded border p-1.5 text-[10px]"
                          >
                            <div className="text-zinc-700 mb-0.5">
                              {format(
                                new Date(ci.check_in_time!),
                                'MMM d, yyyy',
                              )}
                            </div>
                            {ci.general_notes && (
                              <p className="text-zinc-900 line-clamp-2">
                                {ci.general_notes}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Active issues */}
                  {activeBarriers.length > 0 && (
                    <div>
                      <h3 className="text-[10px] font-medium text-zinc-700 uppercase tracking-wide mb-1.5">
                        Active Issues ({activeBarriers.length})
                      </h3>
                      <div className="space-y-1.5">
                        {activeBarriers.map((barrier) => (
                          <div
                            key={barrier.id}
                            className="bg-red-50 border border-red-100 rounded p-1.5 text-[10px]"
                          >
                            {editingIssue?.id === barrier.id ? (
                              <div className="space-y-1.5">
                                <Textarea
                                  value={editingIssueNotes}
                                  onChange={(e) =>
                                    setEditingIssueNotes(e.target.value)
                                  }
                                  rows={2}
                                  className="text-[10px] resize-none"
                                />
                                <div className="flex justify-end gap-1">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setEditingIssue(null);
                                      setEditingIssueNotes('');
                                    }}
                                    className="h-5 text-[10px] px-1.5"
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    onClick={handleSaveIssueUpdate}
                                    className="h-5 text-[10px] px-1.5"
                                  >
                                    Save
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-start justify-between gap-1">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1 text-red-800 font-medium">
                                    <AlertCircle className="h-2.5 w-2.5 flex-shrink-0" />
                                    {
                                      barrierTypes.find(
                                        (t) => t.value === barrier.barrier_type,
                                      )?.label
                                    }
                                  </div>
                                  <p className="text-red-700 mt-0.5 line-clamp-2">
                                    {barrier.notes}
                                  </p>
                                </div>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="h-5 w-5 p-0 text-red-400 hover:text-red-600 hover:bg-red-100 flex-shrink-0"
                                    >
                                      <MoreHorizontal className="h-3 w-3" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem
                                      onClick={() => handleUpdateIssue(barrier)}
                                      className="text-xs"
                                    >
                                      <Pencil className="mr-1.5 h-3 w-3" />
                                      Update
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() =>
                                        handleResolveIssue(barrier)
                                      }
                                      className="text-xs"
                                    >
                                      <CheckCircle2 className="mr-1.5 h-3 w-3" />
                                      Resolve
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() =>
                                        handleDeleteIssue(barrier.id)
                                      }
                                      className="text-xs text-red-600"
                                    >
                                      <Trash2 className="mr-1.5 h-3 w-3" />
                                      Delete
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Flag issue */}
                  <div className="pt-1">
                    <button
                      type="button"
                      onClick={() => setShowFlagIssue(!showFlagIssue)}
                      className="flex items-center gap-1 text-[10px] text-zinc-700 hover:text-zinc-900"
                    >
                      <Flag className="h-3 w-3" />
                      Flag an Issue
                      <ChevronDown
                        className={`h-3 w-3 transition-transform ${showFlagIssue ? 'rotate-180' : ''}`}
                      />
                    </button>

                    <AnimatePresence>
                      {showFlagIssue && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.15 }}
                          className="overflow-hidden"
                        >
                          <div className="pt-1.5 space-y-1.5">
                            <Select
                              value={barrierType}
                              onValueChange={(v) =>
                                setBarrierType(v as TreatmentBarrierType)
                              }
                            >
                              <SelectTrigger className="h-7 text-[10px]">
                                <SelectValue placeholder="Issue type" />
                              </SelectTrigger>
                              <SelectContent>
                                {barrierTypes.map((type) => (
                                  <SelectItem
                                    key={type.value}
                                    value={type.value}
                                    className="text-[10px]"
                                  >
                                    {type.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Textarea
                              value={barrierNotes}
                              onChange={(e) => setBarrierNotes(e.target.value)}
                              placeholder="Describe the issue..."
                              rows={2}
                              className="text-[10px] resize-none"
                            />
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={handleAddBarrier}
                              disabled={!barrierType || !barrierNotes}
                              className="h-6 text-[10px] w-full"
                            >
                              Add Issue
                            </Button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                {/* Right column - Form */}
                <div className="col-span-2 p-4 space-y-4">
                  {/* General notes */}
                  <FormField
                    control={form.control}
                    name="generalNotes"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-medium text-zinc-900">
                          Call Notes <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            value={field.value ?? ''}
                            rows={2}
                            placeholder="General notes from the call..."
                            className={`resize-none text-sm ${fieldState.error ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Review found providers */}
                  {isFetchingReviewFound &&
                    (!reviewFoundProviders ||
                      reviewFoundProviders.length === 0) && (
                      <div className="text-[11px] text-zinc-500 flex items-center gap-1.5">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Loading review providers…
                      </div>
                    )}
                  {reviewFoundProviders && reviewFoundProviders.length > 0 && (
                    <div className="border rounded-md bg-amber-50/30">
                      <div className="flex items-center justify-between px-3 py-2 border-b">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="h-4 w-4 text-amber-700" />
                          <div>
                            <div className="text-xs font-medium text-zinc-900">
                              Review found providers
                            </div>
                            <div className="text-[11px] text-zinc-600">
                              {reviewFoundProviders.length} pending approval
                            </div>
                          </div>
                        </div>
                        {(isApprovingProvider || isRejectingProvider) && (
                          <div className="text-[11px] text-zinc-600 flex items-center gap-1">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            Updating…
                          </div>
                        )}
                      </div>

                      <div className="px-3 py-2 space-y-2">
                        {reviewFoundProviders.map((p) => (
                          <div
                            key={p.medical_provider_id}
                            className="bg-white rounded border p-2 flex items-start justify-between gap-3"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="text-xs font-medium text-zinc-900 truncate">
                                {p.name ?? 'Unspecified provider'}
                              </div>
                              <div className="text-[11px] text-zinc-600 mt-0.5 line-clamp-2">
                                {p.review_summary ||
                                  p.latest_mention?.reasoning ||
                                  'Needs approval'}
                              </div>
                              {p.latest_mention?.excerpts &&
                                p.latest_mention.excerpts.length > 0 && (
                                  <div className="text-[11px] text-zinc-500 mt-1 line-clamp-2">
                                    “{p.latest_mention.excerpts[0]}”
                                  </div>
                                )}
                            </div>

                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  handleRejectReviewProvider(
                                    p.medical_provider_id,
                                  )
                                }
                                disabled={
                                  isApprovingProvider || isRejectingProvider
                                }
                                className="h-7 text-[11px]"
                              >
                                Reject
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                onClick={() =>
                                  handleApproveReviewProvider(
                                    p.medical_provider_id,
                                  )
                                }
                                disabled={
                                  isApprovingProvider || isRejectingProvider
                                }
                                className="h-7 text-[11px]"
                              >
                                Approve
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Provider updates */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xs font-medium text-zinc-900">
                        Provider Updates
                      </h3>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleAddProvider}
                        className="h-6 text-[10px]"
                      >
                        <Plus className="h-2.5 w-2.5 mr-1" />
                        Add
                      </Button>
                    </div>

                    {fields.length === 0 ? (
                      <div className="text-center py-6 border border-dashed rounded">
                        <p className="text-xs text-zinc-700">
                          No providers for this matter
                        </p>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={handleAddProvider}
                          className="mt-1.5 text-[10px] h-6"
                        >
                          <Plus className="h-2.5 w-2.5 mr-1" />
                          Add first provider
                        </Button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        {fields.map((field, index) => (
                          <div
                            key={field.updateId}
                            className="border rounded p-2.5 space-y-2 bg-zinc-50/30"
                          >
                            {/* Provider header */}
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-zinc-900">
                                {field.providerName || 'Provider'}
                              </span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveProvider(index)}
                                className="h-5 w-5 p-0 text-zinc-500 hover:text-red-600"
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>

                            {/* 2x2 grid */}
                            <div className="grid grid-cols-2 gap-1.5">
                              <FormField
                                control={form.control}
                                name={`providerUpdates.${index}.treatmentStatus`}
                                render={({ field: f }) => (
                                  <FormItem>
                                    <FormLabel className="text-[10px] text-zinc-700">
                                      Status
                                    </FormLabel>
                                    <Select
                                      onValueChange={f.onChange}
                                      value={f.value ?? undefined}
                                    >
                                      <FormControl>
                                        <SelectTrigger className="h-7 text-[11px]">
                                          <SelectValue placeholder="—" />
                                        </SelectTrigger>
                                      </FormControl>
                                      <SelectContent>
                                        {Object.entries(
                                          treatmentStatusInfo,
                                        ).map(([key, info]) => (
                                          <SelectItem
                                            key={key}
                                            value={key}
                                            className="text-xs"
                                          >
                                            {info.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={form.control}
                                name={`providerUpdates.${index}.treatmentProgress`}
                                render={({ field: f }) => (
                                  <FormItem>
                                    <FormLabel className="text-[10px] text-zinc-700">
                                      Progress
                                    </FormLabel>
                                    <Select
                                      onValueChange={f.onChange}
                                      value={f.value ?? undefined}
                                    >
                                      <FormControl>
                                        <SelectTrigger className="h-7 text-[11px]">
                                          <SelectValue placeholder="—" />
                                        </SelectTrigger>
                                      </FormControl>
                                      <SelectContent>
                                        {Object.entries(
                                          treatmentProgressInfo,
                                        ).map(([key, info]) => (
                                          <SelectItem
                                            key={key}
                                            value={key}
                                            className="text-xs"
                                          >
                                            {info.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={form.control}
                                name={`providerUpdates.${index}.treatmentFrequency`}
                                render={({ field: f }) => (
                                  <FormItem>
                                    <FormLabel className="text-[10px] text-zinc-700">
                                      Frequency
                                    </FormLabel>
                                    <Select
                                      onValueChange={f.onChange}
                                      value={f.value ?? undefined}
                                    >
                                      <FormControl>
                                        <SelectTrigger className="h-7 text-[11px]">
                                          <SelectValue placeholder="—" />
                                        </SelectTrigger>
                                      </FormControl>
                                      <SelectContent>
                                        {Object.entries(
                                          treatmentFrequencyInfo,
                                        ).map(([key, info]) => (
                                          <SelectItem
                                            key={key}
                                            value={key}
                                            className="text-xs"
                                          >
                                            {info.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={form.control}
                                name={`providerUpdates.${index}.painLevel`}
                                render={({ field: f }) => (
                                  <FormItem>
                                    <FormLabel className="text-[10px] text-zinc-700">
                                      Pain (1-10)
                                    </FormLabel>
                                    <FormControl>
                                      <Input
                                        type="number"
                                        {...f}
                                        value={f.value ?? ''}
                                        placeholder="—"
                                        min={1}
                                        max={10}
                                        className="h-7 text-[11px]"
                                      />
                                    </FormControl>
                                  </FormItem>
                                )}
                              />
                            </div>

                            {/* Last appointment */}
                            <FormField
                              control={form.control}
                              name={`providerUpdates.${index}.lastAppointment`}
                              render={({ field: f }) => (
                                <FormItem>
                                  <FormLabel className="text-[10px] text-zinc-700">
                                    Last Appointment
                                  </FormLabel>
                                  <FormControl>
                                    <DatePicker
                                      className="w-full h-7 text-[11px] justify-start"
                                      date={
                                        f.value
                                          ? new Date(`${f.value}T00:00:00`)
                                          : undefined
                                      }
                                      onDateChange={(date) => {
                                        f.onChange(
                                          date
                                            ? format(date, 'yyyy-MM-dd')
                                            : undefined,
                                        );
                                      }}
                                      placeholder="Select date"
                                    />
                                  </FormControl>
                                </FormItem>
                              )}
                            />

                            {/* Notes */}
                            <FormField
                              control={form.control}
                              name={`providerUpdates.${index}.notes`}
                              render={({ field: f }) => (
                                <FormItem>
                                  <FormLabel className="text-[10px] text-zinc-700">
                                    Notes
                                  </FormLabel>
                                  <FormControl>
                                    <Textarea
                                      {...f}
                                      value={f.value ?? ''}
                                      rows={2}
                                      placeholder="Provider notes..."
                                      className="text-[11px] resize-none"
                                    />
                                  </FormControl>
                                </FormItem>
                              )}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 flex items-center justify-end gap-2 px-4 py-3 border-t bg-zinc-50">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onOpenChange(false)}
                className="h-8 text-xs"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                size="sm"
                className="h-8 text-xs"
              >
                {isSubmitting && (
                  <Loader2 className="h-3 w-3 animate-spin mr-1.5" />
                )}
                Do Check-in
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>

      {/* Make Call Modal */}
      <MakeCallModal
        isOpen={isCallModalOpen}
        onClose={() => setIsCallModalOpen(false)}
        initialPhoneNumber={clientPhone ?? undefined}
        initialContactLabel={clientName}
        matterId={matterId}
        source="check_in_modal"
      />

      {checkInId && (
        <AddProviderModal
          isOpen={isAddProviderModalOpen}
          onClose={() => setIsAddProviderModalOpen(false)}
          matterId={matterId}
          centerLat={incidentCenter?.lat}
          centerLng={incidentCenter?.lng}
          onProviderCreated={async (providerId, providerName) => {
            const existing = form
              .getValues('providerUpdates')
              .some((p) => p.providerId === providerId);
            if (existing) {
              toast.info('That provider is already in this check-in.');
              setIsAddProviderModalOpen(false);
              return;
            }

            try {
              const created = await addProviderUpdate({
                matterId,
                checkInId,
                providerUpdate: {
                  check_in_id: checkInId,
                  provider_id: providerId,
                  provider_name: providerName ?? null,
                },
              }).unwrap();

              append({
                updateId: created.id,
                providerId: created.provider_id ?? providerId,
                providerName:
                  created.provider_name ?? providerName ?? 'Provider',
                treatmentStatus: created.new_treatment_status ?? null,
                treatmentProgress: created.new_treatment_progress ?? null,
                treatmentFrequency: created.new_treatment_frequency ?? null,
                painLevel: created.new_pain_level ?? null,
                notes: created.new_treatment_notes ?? null,
                lastAppointment:
                  created.new_treatment_status_occurred_at ?? null,
              });

              setIsAddProviderModalOpen(false);
            } catch {
              toast.error('Failed to add provider to check-in');
            }
          }}
        />
      )}
    </Dialog>
  );
};

export default CheckInModal;
